import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Paper,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Divider,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  PlayArrow as PlayIcon,
  SkipNext as SkipIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  Replay as ReplayIcon,
  Compare as CompareIcon,
  ContentCopy as ContentCopyIcon,
  Stop as StopIcon,
  Check as CheckIcon,
  ArrowBack as ArrowBackIcon,
  RestartAlt as ResetIcon,
  Language as WebIcon,
  Psychology as SynapseIcon,
} from '@mui/icons-material';
import { useUserInfoContext } from '../../lib/contexts/UserInfoContext';
import { useChatSessionsContext } from '../../lib/contexts/ChatSessionsContext';
import { Exchange, ChatSession } from '../../lib/interfaces/Chat';
import { Sources } from '../../lib/models/Chat';
import { Persona } from '../../lib/api/Persona';
import { postChatMessageStream } from '../../lib/api/Chat';
import { consumeChatStream } from '../../lib/helpers/chatStream';
import { MarkdownContent } from '../Chat/MarkdownContent';
import { ClassificationBadge, SafetyRoute } from '../ClassificationBadge/ClassificationBadge';
import { WebSearchBadge } from '../Chat/WebSearchBadge';
import { buildChatSession, upsertChatSession } from '../../lib/helpers/chatSessionBuilders';

export interface ReplayModalProps {
  open: boolean;
  onClose: () => void;
  originalChatId?: string;
  originalUserId?: string;
  originalUserEmail?: string;
  originalPersonaId?: string;
  originalPersonaName?: string;
  originalTitle?: string;
  originalConversation: Exchange[];
  personas?: Persona[];
}

export interface ReplayedTurn {
  turnIndex: number;
  userQuestion: string;
  newResponse: string;
  newExchangeId?: string;
  newSources?: Sources;
  hasError?: boolean;
  newMetadata?: {
    safetyRoute?: SafetyRoute;
    responseMode?: string;
    workflowMetadata?: any;
    safetyClassification?: any;
    retrievalDecision?: any;
    toolTrace?: any;
    goal?: any;
    useSynapse?: boolean;
  };
  isStreaming?: boolean;
  originalExchange?: Exchange;
}

const parseMeta = (rawMeta: any) => {
  if (!rawMeta) return {};
  let metaObj = rawMeta;
  if (typeof rawMeta === 'string') {
    try {
      metaObj = JSON.parse(rawMeta);
    } catch {
      metaObj = {};
    }
  }
  let safetyRoute: SafetyRoute | undefined;
  if (metaObj?.safetyClassification?.route) {
    safetyRoute = metaObj.safetyClassification.route;
  } else if (metaObj?.safetyRoute) {
    safetyRoute = metaObj.safetyRoute;
  }
  return {
    safetyRoute,
    responseMode: metaObj?.responseMode,
    workflowMetadata: metaObj?.workflowMetadata || undefined,
    safetyClassification: metaObj?.safetyClassification,
    retrievalDecision: metaObj?.retrievalDecision,
    toolTrace: metaObj?.toolTrace,
    goal: metaObj?.goal,
  };
};

const extractOriginalMeta = (originalExchange?: Exchange) => {
  if (!originalExchange) return {};
  const parsed = parseMeta(originalExchange.metadata || originalExchange.workflowMetadata);
  return {
    ...parsed,
    ...(originalExchange.sources && Object.keys(originalExchange.sources).length > 0
      ? { sources: originalExchange.sources }
      : {}),
    ...(originalExchange.seeds?.length ? { seeds: originalExchange.seeds } : {}),
    ...(originalExchange.retrievedChunkIds?.length ? { retrievedChunkIds: originalExchange.retrievedChunkIds } : {}),
    ...(originalExchange.usedChunkIds?.length ? { usedChunkIds: originalExchange.usedChunkIds } : {}),
    ...(originalExchange.rating !== undefined ? { rating: originalExchange.rating } : {}),
    ...(originalExchange.comment ? { comment: originalExchange.comment } : {}),
    ...(originalExchange.feedbackMode ? { feedbackMode: originalExchange.feedbackMode } : {}),
    ...(originalExchange.timestamp || originalExchange.createdAt
      ? { timestamp: originalExchange.timestamp || originalExchange.createdAt }
      : {}),
  };
};

export const ReplayModal: React.FC<ReplayModalProps> = ({
  open,
  onClose,
  originalChatId,
  originalUserId,
  originalUserEmail,
  originalPersonaId,
  originalPersonaName,
  originalTitle,
  originalConversation,
  personas = [],
}) => {
  const { userInfo } = useUserInfoContext();
  const { chatSessions, setChatSessions } = useChatSessionsContext();

  const [queueIndex, setQueueIndex] = useState(0);
  const [currentDraft, setCurrentDraft] = useState('');
  const [replayedTurns, setReplayedTurns] = useState<ReplayedTurn[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [showGlobalOriginals, setShowGlobalOriginals] = useState(true);
  const [expandedTurnMetadata, setExpandedTurnMetadata] = useState<Record<number, boolean>>({});
  const [turnShowOriginalMap, setTurnShowOriginalMap] = useState<Record<number, boolean>>({});
  const [newChatId, setNewChatId] = useState('');
  const [replayNumber, setReplayNumber] = useState(1);
  const [copied, setCopied] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [selectedPersonaId, setSelectedPersonaId] = useState<string>(originalPersonaId || '');
  const [forceWebSearch, setForceWebSearch] = useState(false);
  const [useSynapse, setUseSynapse] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const wasOpenRef = useRef(false);

  // Sync selectedPersonaId when originalPersonaId changes or modal opens
  useEffect(() => {
    if (open) {
      setSelectedPersonaId(originalPersonaId || '');
    }
  }, [open, originalPersonaId]);

  // Resolve matching persona object (supports dynamic selection)
  const persona = useMemo(() => {
    const targetId = selectedPersonaId || originalPersonaId;
    return (
      personas.find((p) => p.id === targetId) ||
      ({
        id: targetId || 'unknown',
        name: (targetId === originalPersonaId ? originalPersonaName : undefined) || 'Assistant',
      } as Persona)
    );
  }, [personas, selectedPersonaId, originalPersonaId, originalPersonaName]);

  // Initialize modal state ONLY when `open` transitions from false to true
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      wasOpenRef.current = true;
      setSelectedPersonaId(originalPersonaId || '');
      setForceWebSearch(false);
      setUseSynapse(false);

      const replayCount = chatSessions.filter((s) =>
        s.chatInfo.chatTitle.includes('[Replay')
      ).length;
      setReplayNumber(replayCount + 1);

      const generatedId = `replay-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
      setNewChatId(generatedId);
      setQueueIndex(0);
      setReplayedTurns([]);
      setIsStreaming(false);
      setExpandedTurnMetadata({});
      setTurnShowOriginalMap({});
      setErrorMessage(null);

      if (originalConversation && originalConversation.length > 0) {
        setCurrentDraft(originalConversation[0].question || '');
      } else {
        setCurrentDraft('');
      }
    } else if (!open && wasOpenRef.current) {
      wasOpenRef.current = false;
      // Abort any active stream if modal closes
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    }
  }, [open, originalConversation, chatSessions, originalPersonaId]);

  // Clean up streaming on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Smooth scroll to bottom when new turns arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [replayedTurns.length, isStreaming]);

  const handleStopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
  };

  const handleNextStep = async () => {
    const questionToSend = currentDraft.trim();
    if (!questionToSend || isStreaming || !userInfo?.accessToken) return;

    setErrorMessage(null);
    const originalTurn = originalConversation[queueIndex];
    const turnIdx = replayedTurns.length;

    const initialTurn: ReplayedTurn = {
      turnIndex: turnIdx,
      userQuestion: questionToSend,
      newResponse: '',
      isStreaming: true,
      originalExchange: originalTurn,
    };

    setReplayedTurns((prev) => [...prev, initialTurn]);
    setIsStreaming(true);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Build conversation history for stream context (excluding failed/error turns)
    const conversationHistory = replayedTurns
      .filter((t) => !t.hasError && t.newResponse && t.newResponse !== 'An error occurred while streaming the response.')
      .flatMap((t) => [
        { role: 'user' as const, content: t.userQuestion },
        { role: 'assistant' as const, content: t.newResponse },
      ]);
    conversationHistory.push({ role: 'user' as const, content: questionToSend });

    let streamSucceeded = false;

    try {
      const response = await postChatMessageStream(
        questionToSend,
        newChatId,
        userInfo.accessToken,
        persona,
        conversationHistory,
        abortController.signal,
        {
          forceWebSearch: forceWebSearch || undefined,
          useSynapse: useSynapse || undefined,
        }
      );

      if (!response || !response.ok) {
        throw new Error(`HTTP error ${response?.status || 'no_response'}`);
      }

      let accumulatedContent = '';
      let latestUpdate: any = null;

      await consumeChatStream(
        response,
        (update) => {
          latestUpdate = update;
          accumulatedContent = update.annotatedAnswer || update.accumulatedContent || accumulatedContent;

          setReplayedTurns((prev) =>
            prev.map((t, idx) => {
              if (idx !== turnIdx) return t;
              const meta = parseMeta(update.workflowMetadata || update);
              return {
                ...t,
                newResponse: accumulatedContent,
                newExchangeId: update.id || t.newExchangeId,
                newSources: update.sources || t.newSources,
                newMetadata: {
                  safetyRoute: update.safetyRoute || meta.safetyRoute,
                  responseMode: update.responseMode || meta.responseMode,
                  workflowMetadata: update.workflowMetadata || meta.workflowMetadata,
                  safetyClassification: update.safetyClassification || meta.safetyClassification,
                  retrievalDecision: update.retrievalDecision || meta.retrievalDecision,
                  toolTrace: update.toolTrace || meta.toolTrace,
                  goal: update.goal || meta.goal,
                  useSynapse: update.useSynapse,
                },
              };
            })
          );
        },
        abortController.signal
      );

      setReplayedTurns((prev) =>
        prev.map((t, idx) => {
          if (idx !== turnIdx) return t;
          const meta = parseMeta(latestUpdate?.workflowMetadata || latestUpdate);
          return {
            ...t,
            newResponse: accumulatedContent,
            isStreaming: false,
            newExchangeId: latestUpdate?.id || t.newExchangeId,
            newSources: latestUpdate?.sources || t.newSources,
            newMetadata: {
              safetyRoute: latestUpdate?.safetyRoute || meta.safetyRoute,
              responseMode: latestUpdate?.responseMode || meta.responseMode,
              workflowMetadata: latestUpdate?.workflowMetadata || meta.workflowMetadata,
              safetyClassification: latestUpdate?.safetyClassification || meta.safetyClassification,
              retrievalDecision: latestUpdate?.retrievalDecision || meta.retrievalDecision,
              toolTrace: latestUpdate?.toolTrace || meta.toolTrace,
              goal: latestUpdate?.goal || meta.goal,
              useSynapse: latestUpdate?.useSynapse,
            },
          };
        })
      );

      // Save/Upsert replayed session into admin user's chat session history without hijacking active selection
      const titleTag = `[Replay #${replayNumber}] ${originalTitle || persona.name || 'Conversation'}`;
      setChatSessions((prev: ChatSession[]) =>
        upsertChatSession(prev, buildChatSession(titleTag, persona.id, newChatId))
      );

      streamSucceeded = true;
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        console.log('Replay stream aborted by user');
      } else {
        console.error('Replay step failed:', err);
        setErrorMessage(err?.message || 'An error occurred while streaming the response.');
      }
      setReplayedTurns((prev) =>
        prev.map((t, idx) => {
          if (idx !== turnIdx) return t;
          return {
            ...t,
            newResponse: t.newResponse || 'An error occurred while streaming the response.',
            isStreaming: false,
            hasError: true,
          };
        })
      );
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;

      // Advance queue ONLY if the step succeeded
      if (streamSucceeded) {
        const nextIdx = queueIndex + 1;
        setQueueIndex(nextIdx);
        if (nextIdx < originalConversation.length) {
          setCurrentDraft(originalConversation[nextIdx].question || '');
        } else {
          setCurrentDraft('');
        }
      }
    }
  };

  const originalTurn = originalConversation[queueIndex];

  const handlePreviousTurn = () => {
    if (isStreaming || queueIndex <= 0) return;
    const prevIdx = queueIndex - 1;
    setQueueIndex(prevIdx);
    setCurrentDraft(originalConversation[prevIdx]?.question || '');
  };

  const handleSkipTurn = () => {
    if (isStreaming) return;
    const nextIdx = queueIndex + 1;
    setQueueIndex(nextIdx);
    if (nextIdx < originalConversation.length) {
      setCurrentDraft(originalConversation[nextIdx].question || '');
    } else {
      setCurrentDraft('');
    }
  };

  const handleClearText = () => {
    setCurrentDraft('');
  };

  const handleResetText = () => {
    if (originalTurn) {
      setCurrentDraft(originalTurn.question || '');
    }
  };

  const toggleTurnMetadata = (turnIdx: number) => {
    setExpandedTurnMetadata((prev) => ({ ...prev, [turnIdx]: !prev[turnIdx] }));
  };

  const toggleTurnOriginal = (turnIdx: number) => {
    setTurnShowOriginalMap((prev) => ({
      ...prev,
      [turnIdx]: prev[turnIdx] === undefined ? !showGlobalOriginals : !prev[turnIdx],
    }));
  };

  const handleCopyLog = () => {
    if (replayedTurns.length === 0) return;
    const header = [
      `=== CONVERSATION REPLAY LOG ===`,
      `Original Session ID: ${originalChatId || 'N/A'}`,
      `Original User: ${originalUserEmail || originalUserId || 'Unknown'}`,
      `Original Persona: ${originalPersonaName || originalPersonaId || 'Unknown'}`,
      `Live Replay Persona: ${persona.name || persona.id}`,
      `Timestamp: ${new Date().toISOString()}`,
      `Total Turns Replayed: ${replayedTurns.length}`,
      `==============================\n`,
    ].join('\n');

    const turnsText = replayedTurns
      .map((t, idx) => {
        const origAnswer = t.originalExchange?.response || t.originalExchange?.answer || 'N/A';
        const safetyInfo = t.newMetadata?.safetyRoute ? ` [Route: ${t.newMetadata.safetyRoute}]` : '';
        return `[Turn ${idx + 1}]\nUser: ${t.userQuestion}\n\nNew Live Assistant${safetyInfo}: ${t.newResponse}\n\nOriginal Assistant: ${origAnswer}`;
      })
      .join('\n\n---\n\n');

    const text = `${header}\n${turnsText}`;

    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      })
      .catch((err) => {
        console.error('Failed to copy replay log:', err);
      });
  };

  const isQueueComplete = queueIndex >= (originalConversation?.length || 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh', display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Modal Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          justify: 'space-between',
          alignItems: 'center',
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <ReplayIcon color="primary" />
          <Box>
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Replaying:
              </Typography>
              {personas && personas.length > 0 ? (
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <Select
                    value={persona.id}
                    onChange={(e) => setSelectedPersonaId(e.target.value)}
                    disabled={isStreaming}
                    sx={{ fontSize: '0.875rem', height: 32 }}
                  >
                    {!personas.some((p) => p.id === persona.id) && (
                      <MenuItem value={persona.id} disabled sx={{ fontSize: '0.875rem' }}>
                        {persona.name || persona.id} (Archived/Unavailable)
                      </MenuItem>
                    )}
                    {personas.map((p) => (
                      <MenuItem key={p.id} value={p.id} sx={{ fontSize: '0.875rem' }}>
                        {p.name} {p.id === originalPersonaId ? '(Original)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Chip
                  label={persona.name || 'Assistant'}
                  color="primary"
                  size="small"
                  variant="outlined"
                />
              )}
              <Chip
                icon={<ReplayIcon fontSize="small" />}
                label={`Replay #${replayNumber}`}
                color="primary"
                size="small"
                variant="outlined"
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Original User: {originalUserEmail || originalUserId || 'Unknown'} | Original Session: {originalChatId}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <FormControlLabel
            control={
              <Switch
                checked={forceWebSearch}
                onChange={(e) => setForceWebSearch(e.target.checked)}
                size="small"
                disabled={isStreaming}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={0.5}>
                <WebIcon sx={{ fontSize: 16, color: forceWebSearch ? 'primary.main' : 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Web Search</Typography>
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={useSynapse}
                onChange={(e) => setUseSynapse(e.target.checked)}
                size="small"
                disabled={isStreaming}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={0.5}>
                <SynapseIcon sx={{ fontSize: 16, color: useSynapse ? 'primary.main' : 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Synapse</Typography>
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={showGlobalOriginals}
                onChange={(e) => setShowGlobalOriginals(e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Show Originals</Typography>}
          />
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

        {errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ borderRadius: 0 }}>
            {errorMessage}
          </Alert>
        )}

      {/* Main Conversation Thread */}
      <DialogContent
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          bgcolor: 'background.default',
        }}
      >
        {replayedTurns.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Ready to Step Through Conversation Replay
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Review or edit the user input below and click <strong>Send / Next Step</strong> to generate live responses from the LLM turn by turn.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total original turns in queue: {originalConversation.length}
            </Typography>
          </Paper>
        ) : (
          replayedTurns.map((turn) => {
            const showOriginalForTurn =
              turnShowOriginalMap[turn.turnIndex] !== undefined
                ? turnShowOriginalMap[turn.turnIndex]
                : showGlobalOriginals;
            const originalExchange = turn.originalExchange;
            const originalMeta = extractOriginalMeta(originalExchange);
            const isFollowUp = turn.turnIndex >= (originalConversation?.length || 0);

            return (
              <Box key={turn.turnIndex} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    {isFollowUp
                      ? `TURN ${turn.turnIndex + 1} (Follow-Up)`
                      : `TURN ${turn.turnIndex + 1} OF ${originalConversation.length}`}
                  </Typography>
                  <Box display="flex" gap={1}>
                    {originalExchange && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => toggleTurnOriginal(turn.turnIndex)}
                        startIcon={<CompareIcon />}
                      >
                        {showOriginalForTurn ? 'Hide Original' : 'Show Original'}
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => toggleTurnMetadata(turn.turnIndex)}
                      startIcon={
                        <ExpandMoreIcon
                          sx={{
                            transform: expandedTurnMetadata[turn.turnIndex] ? 'rotate(180deg)' : 'none',
                          }}
                        />
                      }
                    >
                      Compare Metadata
                    </Button>
                  </Box>
                </Box>

                {/* Replayed User Input */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    alignSelf: 'flex-end',
                    maxWidth: '85%',
                    boxShadow: 1,
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 600, display: 'block', mb: 0.5 }}>
                    User (Replayed)
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {turn.userQuestion}
                  </Typography>
                  {originalExchange && turn.userQuestion !== originalExchange.question && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.75, fontStyle: 'italic' }}>
                      (Edited from original: "{originalExchange.question}")
                    </Typography>
                  )}
                </Box>

                {/* Side-by-side or Stacked Comparison for Assistant Responses */}
                <Box sx={{ display: 'grid', gridTemplateColumns: showOriginalForTurn ? { xs: '1fr', md: '1fr 1fr' } : '1fr', gap: 2 }}>
                  {/* NEW LIVE RESPONSE */}
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'primary.light',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                        {persona.name || 'Assistant'} (New Live Response)
                      </Typography>
                      <Box display="flex" gap={0.5}>
                        {turn.newMetadata?.safetyRoute && <ClassificationBadge safetyRoute={turn.newMetadata.safetyRoute} />}
                        {turn.newMetadata?.responseMode === 'webSearch' && <WebSearchBadge />}
                      </Box>
                    </Box>

                    {turn.isStreaming && !turn.newResponse ? (
                      <Box display="flex" alignItems="center" gap={1} py={2}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">Generating live response...</Typography>
                      </Box>
                    ) : (
                      <MarkdownContent content={turn.newResponse || ''} sources={turn.newSources} />
                    )}
                  </Paper>

                  {/* ORIGINAL TRANSCRIPT RESPONSE */}
                  {showOriginalForTurn && originalExchange && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px dashed',
                        borderColor: 'divider',
                        bgcolor: 'action.hover',
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Original Response ({originalUserEmail || 'Transcript'})
                        </Typography>
                        <Box display="flex" gap={0.5}>
                          {originalMeta.safetyRoute && <ClassificationBadge safetyRoute={originalMeta.safetyRoute} />}
                          {originalMeta.responseMode === 'webSearch' && <WebSearchBadge />}
                        </Box>
                      </Box>

                      <MarkdownContent content={originalExchange.response || originalExchange.answer || ''} sources={originalExchange.sources} />
                    </Paper>
                  )}
                </Box>

                {/* METADATA COMPARISON INSPECTOR */}
                {expandedTurnMetadata[turn.turnIndex] && (
                  <Accordion defaultExpanded sx={{ bgcolor: 'action.selected', borderRadius: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Turn {turn.turnIndex + 1} Metadata Comparison (Old vs New)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        {/* COLUMN 1: ORIGINAL BASELINE */}
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>
                            ORIGINAL TRANSCRIPT METADATA (BASELINE)
                          </Typography>
                          <Paper sx={{ p: 1.5, fontFamily: 'monospace', fontSize: '0.75rem', overflowX: 'auto', bgcolor: 'background.paper' }}>
                            <pre style={{ margin: 0 }}>{JSON.stringify(originalMeta || {}, null, 2)}</pre>
                          </Paper>
                        </Box>

                        {/* COLUMN 2: NEW LIVE */}
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', display: 'block', mb: 1 }}>
                            NEW LIVE METADATA
                          </Typography>
                          <Paper sx={{ p: 1.5, fontFamily: 'monospace', fontSize: '0.75rem', overflowX: 'auto', bgcolor: 'background.paper' }}>
                            <pre style={{ margin: 0 }}>{JSON.stringify(turn.newMetadata || {}, null, 2)}</pre>
                          </Paper>
                        </Box>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}
                <Divider sx={{ my: 1 }} />
              </Box>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </DialogContent>

      {/* Replay Controller & Step Input Bar */}
      <Paper
        elevation={4}
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {isQueueComplete
              ? 'All queued original turns replayed!'
              : `Upcoming Queue Step ${queueIndex + 1} of ${originalConversation?.length || 0}`}
          </Typography>

          <Box display="flex" gap={1} flexWrap="wrap">
            {queueIndex > 0 && !isStreaming && (
              <Button
                size="small"
                variant="outlined"
                onClick={handlePreviousTurn}
                startIcon={<ArrowBackIcon />}
              >
                Previous Turn
              </Button>
            )}
            {originalTurn && currentDraft !== (originalTurn.question || '') && !isStreaming && (
              <Button
                size="small"
                variant="outlined"
                color="info"
                onClick={handleResetText}
                startIcon={<ResetIcon />}
              >
                Reset to Original
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={handleClearText}
              startIcon={<ClearIcon />}
              disabled={isStreaming || !currentDraft}
            >
              Clear Text
            </Button>
            {!isQueueComplete && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleSkipTurn}
                startIcon={<SkipIcon />}
                disabled={isStreaming}
              >
                Skip Turn
              </Button>
            )}
          </Box>
        </Box>

        <Box display="flex" gap={1.5} alignItems="center">
          <TextField
            fullWidth
            multiline
            maxRows={3}
            placeholder={
              isQueueComplete
                ? 'Type additional follow-up question to continue conversation...'
                : 'Edit queued user question or type custom input...'
            }
            value={currentDraft}
            onChange={(e) => setCurrentDraft(e.target.value)}
            disabled={isStreaming}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleNextStep();
              }
            }}
          />

          {isStreaming ? (
            <Button
              variant="contained"
              color="error"
              size="large"
              onClick={handleStopStreaming}
              startIcon={<StopIcon />}
              sx={{ px: 3, minWidth: 160, height: 56 }}
            >
              Stop Streaming
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleNextStep}
              disabled={!currentDraft.trim()}
              startIcon={<PlayIcon />}
              sx={{ px: 3, minWidth: 160, height: 56 }}
            >
              {isQueueComplete ? 'Send Custom' : 'Send / Next Step'}
            </Button>
          )}
        </Box>
      </Paper>

      <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          onClick={handleCopyLog}
          startIcon={copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
          disabled={replayedTurns.length === 0}
        >
          {copied ? 'Copied Log!' : 'Copy Replay Log'}
        </Button>
        <Button onClick={onClose}>Done / Close</Button>
      </DialogActions>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        message="Replay log copied to clipboard"
      />
    </Dialog>
  );
};

export default ReplayModal;
